/**
 * /online-camera — the arithmetic of a camera, without a camera.
 *
 * Everything here is pure: no DOM, no MediaStream, no timers. The page
 * (index.js and friends) feeds it numbers and pixels; node tests
 * (tests/js/online-camera-core.test.mjs) pin every rule on concrete values.
 *
 * What lives here and why each piece is easy to get quietly wrong:
 *
 *   • Crop geometry. The preview shows the STREAM; the saved picture may be a
 *     full-sensor still from ImageCapture.takePhoto() of another size and even
 *     another shape (a phone streams 16:9 as a centre crop of its 4:3 sensor).
 *     The frame drawn on the preview must land on the same part of the scene in
 *     the file — WYSIWYG is the contract, extra sensor pixels are a bonus.
 *   • Aspect presets that follow the frame's orientation: «4:3» on a phone held
 *     upright is the tall 3:4 a phone camera means by it, while «9:16» on a
 *     laptop webcam is the vertical story crop.
 *   • Filters as the CSS filter functions of the Filter Effects spec, so the
 *     live preview (CSS on the <video>) and the saved file (these matrices, or
 *     ctx.filter where the browser has it) are the same picture.
 *   • Histogram, zebra and focus peaking on a downscaled frame.
 *   • The photo-booth sheet: 2×6″ strip and 4×6″ (10×15 cm) print at 300 dpi.
 *   • Smart shutters: a smile hold, an open palm, a clap that is not speech.
 *   • Frame stacking for the night mode (noise falls as √N on a tripod).
 */

/* ───────────────────────── aspect ratios & crop ───────────────────────── */

export const ASPECT_KEYS = ['full', '1:1', '4:3', '3:2', '16:9', '9:16'];

/**
 * The actual width:height of a preset for a frame of this shape.
 * 4:3, 3:2, 16:9 follow the frame (tall frame → tall crop); 1:1 is square;
 * 9:16 is always the OTHER orientation of 16:9 (a vertical story on a laptop
 * webcam, a landscape band on a phone held upright); 'full' is the frame.
 */
export function orientAspect(key, frameW, frameH) {
    if (Array.isArray(key) && key[0] > 0 && key[1] > 0) return [key[0], key[1]];   // an exact shape (a booth cell)
    if (!key || key === 'full' || !(frameW > 0) || !(frameH > 0)) return [frameW, frameH];
    const m = /^(\d+):(\d+)$/.exec(key);
    if (!m) return [frameW, frameH];
    const a = +m[1], b = +m[2];
    const portrait = frameH > frameW;
    if (a === b) return [1, 1];
    if (key === '9:16') return portrait ? [16, 9] : [9, 16];
    return portrait ? [b, a] : [a, b];
}

/** Label shown on the chip for this frame («4:3» or «3:4»). */
export function aspectLabel(key, frameW, frameH) {
    if (!key || key === 'full' || Array.isArray(key)) return null;
    const [a, b] = orientAspect(key, frameW, frameH);
    return a + ':' + b;
}

/** The largest centred rectangle of aspect aw:ah inside W×H, in whole pixels. */
export function centerCrop(W, H, aw, ah) {
    W = Math.max(1, Math.round(W)); H = Math.max(1, Math.round(H));
    if (!(aw > 0) || !(ah > 0)) return { x: 0, y: 0, w: W, h: H };
    const target = aw / ah;
    let w = W, h = Math.round(W / target);
    if (h > H) { h = H; w = Math.round(H * target); }
    w = Math.min(W, Math.max(1, w));
    h = Math.min(H, Math.max(1, h));
    return { x: Math.floor((W - w) / 2), y: Math.floor((H - h) / 2), w, h };
}

/**
 * Where the crop frame seen on the preview lands in a captured picture.
 *
 * The stream's field of view is taken as the centred region of the picture
 * with the STREAM's aspect (that is how cameras derive a 16:9 stream from a
 * 4:3 sensor); the frame is centred inside that region; digital zoom (≥1)
 * shrinks it about the centre. When the picture is exactly the stream frame
 * this reduces to a plain centre crop.
 */
export function mapCrop(streamW, streamH, picW, picH, aspectKey, zoom) {
    const fov = centerCrop(picW, picH, streamW, streamH);
    const [aw, ah] = orientAspect(aspectKey, streamW, streamH);
    const r = centerCrop(fov.w, fov.h, aw, ah);
    const z = zoom > 1 ? zoom : 1;
    if (z > 1) {
        const w = Math.max(1, Math.round(r.w / z)), h = Math.max(1, Math.round(r.h / z));
        r.x += Math.floor((r.w - w) / 2); r.y += Math.floor((r.h - h) / 2);
        r.w = w; r.h = h;
    }
    return { x: fov.x + r.x, y: fov.y + r.y, w: r.w, h: r.h };
}

/**
 * Is a still of picW×picH the same shape family as the stream? A full-sensor
 * still that comes back sideways (portrait stream, landscape still) cannot be
 * mapped honestly — the caller falls back to the stream frame.
 */
export function sameOrientation(streamW, streamH, picW, picH) {
    const s = streamW / streamH, p = picW / picH;
    if (Math.abs(s - 1) < 0.05 || Math.abs(p - 1) < 0.05) return true;
    return (s > 1) === (p > 1);
}

/**
 * Scale an output down so it fits a canvas-area ceiling (iOS Safari refuses
 * canvases over ~16.7 Mpx and draws nothing). Returns integer w/h.
 */
export function fitArea(w, h, maxArea) {
    if (!(maxArea > 0) || w * h <= maxArea) return { w, h, scale: 1 };
    const s = Math.sqrt(maxArea / (w * h));
    return { w: Math.max(1, Math.floor(w * s)), h: Math.max(1, Math.floor(h * s)), scale: s };
}

/* ───────────────────────── resolution presets ───────────────────────── */

/** getUserMedia ideals per preset. 'max' asks for ~12 Mpx 4:3: a phone gives
 *  its full-sensor stream, a 4K webcam its 4K mode, a 1080p webcam 1080p. */
export const RES_PRESETS = {
    max: { w: 4096, h: 3072 },
    2160: { w: 3840, h: 2160 },
    1080: { w: 1920, h: 1080 },
    720: { w: 1280, h: 720 },
};

export function megapixels(w, h) {
    return Math.round((w * h) / 1e5) / 10;
}

/* ───────────────────────── file names ───────────────────────── */

const pad = (n, l = 2) => String(n).padStart(l, '0');

/** «IMG_20260926_143012» from a local date. */
export function stamp(d) {
    return d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate()) + '_' + pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds());
}

export const KIND_PREFIX = { photo: 'IMG', burst: 'IMG', booth: 'BOOTH', night: 'NIGHT', timelapse: 'TIMELAPSE', smile: 'IMG' };
export const FORMAT_EXT = { jpeg: 'jpg', png: 'png', webp: 'webp', gif: 'gif', mp4: 'mp4', zip: 'zip' };
export const FORMAT_MIME = { jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', mp4: 'video/mp4' };

/**
 * A file name that sorts by time in any file manager and never collides:
 * `used` is a Set of names already given in this session (a burst takes
 * several frames inside one second).
 */
export function photoName(d, kind, fmt, seq, used) {
    const pre = KIND_PREFIX[kind] || 'IMG';
    const ext = FORMAT_EXT[fmt] || fmt || 'jpg';
    let base = pre + '_' + stamp(d);
    if (seq != null) base += '_' + (kind === 'burst' ? 'B' : '') + pad(seq, 2);
    let name = base + '.' + ext, n = 2;
    if (used) {
        while (used.has(name)) name = base + '_' + (n++) + '.' + ext;
        used.add(name);
    }
    return name;
}

/** Film-camera date imprint: «’26 09 26». Numbers only — locale-free, like the cameras did it. */
export function dateImprint(d) {
    return '’' + String(d.getFullYear()).slice(-2) + ' ' + pad(d.getMonth() + 1) + ' ' + pad(d.getDate());
}

export function formatBytes(n) {
    if (!(n >= 0)) return '';
    if (n < 1024) return n + ' B';
    if (n < 1048576) return (n / 1024).toFixed(n < 10240 ? 1 : 0) + ' KB';
    if (n < 1073741824) return (n / 1048576).toFixed(n < 10485760 ? 1 : 0) + ' MB';
    return (n / 1073741824).toFixed(2) + ' GB';
}

/* ───────────────────────── manual controls ───────────────────────── */

/**
 * exposureTime in the Image Capture spec is in units of 100 µs. «1/60» is
 * 166.7 units. Shown the way a camera shows it: 1/N below half a second,
 * then seconds with a double prime.
 */
export function formatShutter(units) {
    if (!(units > 0)) return '—';
    const s = units / 10000;
    if (s >= 0.5) return (Math.round(s * 10) / 10) + '″';
    const n = 1 / s;
    const nice = n >= 100 ? Math.round(n / 10) * 10 : Math.round(n);
    return '1/' + nice;
}

/** Geometric slider: equal travel = equal ratio (1/1000 → 1/500 as far as 1/60 → 1/30). */
export function logToValue(pos, min, max) {
    const t = Math.min(1, Math.max(0, pos / 1000));
    const lo = Math.max(min, 1e-6);
    if (!(max > lo)) return lo;
    return lo * Math.pow(max / lo, t);
}
export function valueToLog(v, min, max) {
    const lo = Math.max(min, 1e-6);
    if (!(max > lo)) return 0;
    const t = Math.log(Math.min(max, Math.max(lo, v)) / lo) / Math.log(max / lo);
    return Math.round(t * 1000);
}
/** Snap a value to a capability's step grid inside [min, max]. */
export function snapStep(v, cap) {
    if (!cap) return v;
    let x = Math.min(cap.max, Math.max(cap.min, v));
    if (cap.step > 0) x = cap.min + Math.round((x - cap.min) / cap.step) * cap.step;
    return Math.min(cap.max, Math.max(cap.min, +x.toFixed(6)));
}

/** A numeric capability worth a control: a range with somewhere to go. */
export function usableRange(cap) {
    return !!(cap && typeof cap.min === 'number' && typeof cap.max === 'number' && cap.max > cap.min);
}

/* ───────────────────────── filters (CSS Filter Effects) ───────────────────────── */

/**
 * Looks. `ops` are CSS filter functions in order; `layers` are colour washes
 * composited with a blend mode (CSS mix-blend-mode on the preview, canvas
 * globalCompositeOperation on the file — the same W3C compositing formulas);
 * `vignette` is the darkness at the corners; `grain` the noise amplitude.
 */
export const FILTERS = {
    none: { ops: [] },
    vivid: { ops: [['saturate', 1.35], ['contrast', 1.08]] },
    bw: { ops: [['grayscale', 1], ['contrast', 1.1]] },
    noir: { ops: [['grayscale', 1], ['contrast', 1.5], ['brightness', 0.92]], vignette: 0.5 },
    sepia: { ops: [['sepia', 0.9], ['contrast', 1.04], ['brightness', 1.02]] },
    warm: { ops: [['sepia', 0.2], ['saturate', 1.2], ['brightness', 1.03]], layers: [['rgba(255,150,40,0.18)', 'soft-light']] },
    cool: { ops: [['saturate', 0.92], ['contrast', 1.05]], layers: [['rgba(40,120,255,0.22)', 'soft-light']] },
    film: { ops: [['contrast', 1.12], ['saturate', 1.18], ['sepia', 0.14]], layers: [['rgba(255,190,120,0.16)', 'overlay']], vignette: 0.28, grain: 0.05 },
    fade: { ops: [['contrast', 0.84], ['brightness', 1.08], ['saturate', 0.78]], layers: [['rgba(255,238,220,0.14)', 'screen']] },
    retro: { ops: [['sepia', 0.45], ['hue-rotate', -25], ['saturate', 1.35]], layers: [['rgba(243,106,188,0.16)', 'screen']], vignette: 0.22, grain: 0.04 },
};
export const FILTER_KEYS = Object.keys(FILTERS);

/** The CSS `filter` value for a look (what the <video> preview wears). */
export function cssFilter(key) {
    const f = FILTERS[key];
    if (!f || !f.ops.length) return 'none';
    return f.ops.map(([op, v]) => op === 'hue-rotate' ? `hue-rotate(${v}deg)` : `${op}(${v})`).join(' ');
}

/** 3×3 colour matrices of the Filter Effects spec (row-major, sRGB 0..1). */
export function opMatrix(op, v) {
    switch (op) {
        case 'grayscale': {
            const a = 1 - Math.min(1, Math.max(0, v));
            return [0.2126 + 0.7874 * a, 0.7152 - 0.7152 * a, 0.0722 - 0.0722 * a,
                0.2126 - 0.2126 * a, 0.7152 + 0.2848 * a, 0.0722 - 0.0722 * a,
                0.2126 - 0.2126 * a, 0.7152 - 0.7152 * a, 0.0722 + 0.9278 * a];
        }
        case 'sepia': {
            const a = 1 - Math.min(1, Math.max(0, v));
            return [0.393 + 0.607 * a, 0.769 - 0.769 * a, 0.189 - 0.189 * a,
                0.349 - 0.349 * a, 0.686 + 0.314 * a, 0.168 - 0.168 * a,
                0.272 - 0.272 * a, 0.534 - 0.534 * a, 0.131 + 0.869 * a];
        }
        case 'saturate': {
            const s = Math.max(0, v);
            return [0.213 + 0.787 * s, 0.715 - 0.715 * s, 0.072 - 0.072 * s,
                0.213 - 0.213 * s, 0.715 + 0.285 * s, 0.072 - 0.072 * s,
                0.213 - 0.213 * s, 0.715 - 0.715 * s, 0.072 + 0.928 * s];
        }
        case 'hue-rotate': {
            const r = v * Math.PI / 180, c = Math.cos(r), s = Math.sin(r);
            return [0.213 + c * 0.787 - s * 0.213, 0.715 - c * 0.715 - s * 0.715, 0.072 - c * 0.072 + s * 0.928,
                0.213 - c * 0.213 + s * 0.143, 0.715 + c * 0.285 + s * 0.140, 0.072 - c * 0.072 - s * 0.283,
                0.213 - c * 0.213 - s * 0.787, 0.715 - c * 0.715 + s * 0.715, 0.072 + c * 0.928 + s * 0.072];
        }
        default: return null;
    }
}

/**
 * Apply a look's `ops` to RGBA bytes in place, step by step with clamping
 * after each function — the way a browser chains filter primitives. Used where
 * the canvas has no `filter` property (Safari); elsewhere ctx.filter renders
 * the same CSS string the preview wears.
 */
export function applyOps(data, ops) {
    if (!ops || !ops.length) return data;
    const steps = ops.map(([op, v]) => {
        if (op === 'brightness') return { k: 'lin', m: v, b: 0 };
        if (op === 'contrast') return { k: 'lin', m: v, b: 0.5 - 0.5 * v };
        return { k: 'mat', m: opMatrix(op, v) };
    }).filter(s => s.k === 'lin' || s.m);
    const n = data.length;
    for (let i = 0; i < n; i += 4) {
        let r = data[i] / 255, g = data[i + 1] / 255, b = data[i + 2] / 255;
        for (let s = 0; s < steps.length; s++) {
            const st = steps[s];
            if (st.k === 'lin') {
                r = r * st.m + st.b; g = g * st.m + st.b; b = b * st.m + st.b;
            } else {
                const m = st.m;
                const nr = m[0] * r + m[1] * g + m[2] * b;
                const ng = m[3] * r + m[4] * g + m[5] * b;
                const nb = m[6] * r + m[7] * g + m[8] * b;
                r = nr; g = ng; b = nb;
            }
            r = r < 0 ? 0 : r > 1 ? 1 : r;
            g = g < 0 ? 0 : g > 1 ? 1 : g;
            b = b < 0 ? 0 : b > 1 ? 1 : b;
        }
        data[i] = Math.round(r * 255); data[i + 1] = Math.round(g * 255); data[i + 2] = Math.round(b * 255);
    }
    return data;
}

/** Vignette as an elliptical radial gradient, the same on the preview (CSS) and the file (canvas). */
export const VIGNETTE_INNER = 0.55;
export function vignetteCss(v) {
    if (!(v > 0)) return 'none';
    return `radial-gradient(ellipse at center, rgba(0,0,0,0) ${Math.round(VIGNETTE_INNER * 100)}%, rgba(0,0,0,${v}) 100%)`;
}

/** Deterministic noise (mulberry32) — grain must not change between the preview tile and a test run. */
export function rng(seed) {
    let a = seed >>> 0;
    return function () {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Add monochrome grain of amplitude `amt` (0..1 of full scale) in place. */
export function applyGrain(data, amt, seed) {
    if (!(amt > 0)) return data;
    const r = rng(seed || 7), k = amt * 255 * 2;
    for (let i = 0; i < data.length; i += 4) {
        const d = (r() - 0.5) * k;
        data[i] = clamp8(data[i] + d); data[i + 1] = clamp8(data[i + 1] + d); data[i + 2] = clamp8(data[i + 2] + d);
    }
    return data;
}
const clamp8 = (x) => x < 0 ? 0 : x > 255 ? 255 : Math.round(x);

/* ───────────────────────── analysis ───────────────────────── */

/** Rec.709 luma of gamma-encoded bytes — what a camera's histogram shows. */
export const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/**
 * RGB + luma histogram of RGBA bytes, plus the share of pixels blown out
 * (any channel ≥ 254) and crushed (luma ≤ 1).
 */
export function histogram(data) {
    const r = new Uint32Array(256), g = new Uint32Array(256), b = new Uint32Array(256), l = new Uint32Array(256);
    let clip = 0, crush = 0, n = 0;
    for (let i = 0; i < data.length; i += 4) {
        const R = data[i], G = data[i + 1], B = data[i + 2];
        r[R]++; g[G]++; b[B]++;
        const L = Math.round(luma(R, G, B));
        l[L]++;
        if (R >= 254 || G >= 254 || B >= 254) clip++;
        if (L <= 1) crush++;
        n++;
    }
    return { r, g, b, l, n, clipped: n ? clip / n : 0, crushed: n ? crush / n : 0 };
}

/** 1 where luma ≥ threshold (0..255): the zebra stripes go there. */
export function zebraMask(data, threshold) {
    const out = new Uint8Array(data.length >> 2);
    for (let i = 0, j = 0; i < data.length; i += 4, j++) {
        out[j] = luma(data[i], data[i + 1], data[i + 2]) >= threshold ? 1 : 0;
    }
    return out;
}

/**
 * Focus peaking: Sobel gradient of luma; a pixel is «in focus» when its
 * gradient is both strong in absolute terms and among the strongest of the
 * frame (so a flat grey wall does not light up because of sensor noise, and a
 * contrasty scene does not light up everywhere).
 */
export function peakingMask(data, w, h, sensitivity) {
    const L = new Float32Array(w * h);
    for (let i = 0, j = 0; j < L.length; i += 4, j++) L[j] = luma(data[i], data[i + 1], data[i + 2]);
    const G = new Float32Array(w * h);
    let max = 0;
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const p = y * w + x;
            const gx = -L[p - w - 1] - 2 * L[p - 1] - L[p + w - 1] + L[p - w + 1] + 2 * L[p + 1] + L[p + w + 1];
            const gy = -L[p - w - 1] - 2 * L[p - w] - L[p - w + 1] + L[p + w - 1] + 2 * L[p + w] + L[p + w + 1];
            const m = Math.sqrt(gx * gx + gy * gy);
            G[p] = m;
            if (m > max) max = m;
        }
    }
    const s = Math.min(1, Math.max(0, sensitivity == null ? 0.5 : sensitivity));
    // 0 → only the very sharpest edges; 1 → most visible edges.
    const rel = 0.75 - 0.5 * s;
    const thr = Math.max(90, max * rel);
    const out = new Uint8Array(w * h);
    let lit = 0;
    for (let p = 0; p < G.length; p++) if (G[p] >= thr) { out[p] = 1; lit++; }
    return { mask: out, lit, max };
}

/* ───────────────────────── level ───────────────────────── */

/**
 * From DeviceOrientation β/γ (degrees) and the screen's rotation angle, the
 * horizon tilt as the person sees it. `roll` > 0 means the device is turned
 * clockwise, so the true horizon on screen leans counter-clockwise by the same
 * angle. Near flat (screen facing up/down) there is no horizon to speak of —
 * a top-down shot — and the result switches to a bubble (x, y in −1..1).
 */
export function levelFrom(beta, gamma, screenAngle) {
    if (beta == null || gamma == null || isNaN(beta) || isNaN(gamma)) return null;
    const br = beta * Math.PI / 180, gr = gamma * Math.PI / 180;
    // world «up» in device axes (x right, y top of the device, z out of the screen)
    const ux = -Math.sin(gr) * Math.cos(br);
    const uy = Math.sin(br);
    const uz = Math.cos(gr) * Math.cos(br);
    const planar = Math.hypot(ux, uy);
    if (planar < 0.34) {
        // within ~20° of flat: a bubble, x/y = how far off level (±1 ≈ 20°)
        return { mode: 'flat', x: clampUnit(-ux / 0.34), y: clampUnit(uy / 0.34), face: uz >= 0 ? 'up' : 'down' };
    }
    let roll = Math.atan2(-ux, uy) * 180 / Math.PI;           // device roll, clockwise positive
    // screen.orientation.angle is how far the DEVICE is turned counter-clockwise
    // from its natural orientation; the picture on screen turns back by it.
    roll = norm180(roll + (screenAngle || 0));
    return { mode: 'horizon', roll };
}
const clampUnit = (v) => v < -1 ? -1 : v > 1 ? 1 : v;
function norm180(a) {
    a = ((a + 180) % 360 + 360) % 360 - 180;
    return a === -180 ? 180 : a;
}
/** Snap readings: within ±tol of a right angle the level reads «level». */
export function isLevel(roll, tol) {
    const t = tol == null ? 1 : tol;
    const r = ((roll % 90) + 90) % 90;
    return r <= t || r >= 90 - t;
}

/* ───────────────────────── smart shutters ───────────────────────── */

/** MediaPipe blendshapes → smile 0..1. */
export function smileScore(bs) {
    if (!bs) return 0;
    const l = +bs.mouthSmileLeft || 0, r = +bs.mouthSmileRight || 0;
    return (l + r) / 2;
}

/**
 * Fires once when every watched face has smiled for `holdMs`, then waits for
 * the smiles to drop (hysteresis) before it can fire again — one photo per
 * smile, not a burst for as long as someone keeps smiling.
 */
export class SmileTrigger {
    constructor(opts) {
        const o = opts || {};
        this.threshold = o.threshold == null ? 0.55 : o.threshold;
        this.holdMs = o.holdMs == null ? 350 : o.holdMs;
        this.rearmGap = o.rearmGap == null ? 0.2 : o.rearmGap;
        this.all = !!o.all;
        this._since = -1; this._armed = true;
    }
    /** scores: smile per face; returns true on the frame that should shoot. */
    push(scores, t) {
        const list = (scores || []).filter(s => s >= 0);
        if (!list.length) { this._since = -1; return false; }
        const v = this.all ? Math.min(...list) : Math.max(...list);
        if (!this._armed) {
            if (v < this.threshold - this.rearmGap) this._armed = true;
            return false;
        }
        if (v >= this.threshold) {
            if (this._since < 0) this._since = t;
            if (t - this._since >= this.holdMs) { this._armed = false; this._since = -1; return true; }
        } else this._since = -1;
        return false;
    }
    reset() { this._since = -1; this._armed = true; }
}

const d2 = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
/**
 * An open palm facing the camera: all four fingers straight (tip farther from
 * the wrist than its middle joint by a margin) and the thumb out to the side.
 * `lm` are the 21 MediaPipe hand landmarks.
 */
export function isOpenPalm(lm) {
    if (!lm || lm.length < 21) return false;
    const wrist = lm[0];
    const fingers = [[8, 6], [12, 10], [16, 14], [20, 18]];
    for (const [tip, pip] of fingers) {
        if (!(d2(lm[tip], wrist) > d2(lm[pip], wrist) * 1.12)) return false;
    }
    // thumb tip away from the index knuckle compared with the palm width
    const palmW = d2(lm[5], lm[17]) || 1e-6;
    if (!(d2(lm[4], lm[5]) > palmW * 0.55)) return false;
    // fingers spread a little (a flat hand, not a fist seen edge-on)
    return d2(lm[8], lm[20]) > palmW * 0.9;
}

/** Holds a boolean condition for `holdMs` then fires once, re-arming when it goes false. */
export class HoldTrigger {
    constructor(holdMs) { this.holdMs = holdMs == null ? 600 : holdMs; this._since = -1; this._armed = true; }
    push(on, t) {
        if (!on) { this._since = -1; this._armed = true; return false; }
        if (!this._armed) return false;
        if (this._since < 0) this._since = t;
        if (t - this._since >= this.holdMs) { this._armed = false; return true; }
        return false;
    }
    progress(t) { return this._since < 0 || !this._armed ? 0 : Math.min(1, (t - this._since) / this.holdMs); }
    reset() { this._since = -1; this._armed = true; }
}

/** Peak dBFS of float samples. */
export function peakDb(samples) {
    let p = 0;
    for (let i = 0; i < samples.length; i++) { const a = Math.abs(samples[i]); if (a > p) p = a; }
    return p > 1e-6 ? 20 * Math.log10(p) : -120;
}

/**
 * Clap / loud-sound shutter. A clap is LOUD, SUDDEN and SHORT: it rises by
 * ≥ `rise` dB within ~60 ms, peaks above the calibrated floor + margin, and
 * falls back by ≥ `fall` dB within `confirmMs`. Speech has the loudness but
 * not the shortness — a syllable sustains — so it does not fire. The decision
 * is made `confirmMs` after the peak; one refractory period between shots.
 */
export class ClapDetector {
    constructor(opts) {
        const o = opts || {};
        this.sensitivity = o.sensitivity == null ? 0.5 : o.sensitivity;
        this.rise = o.rise == null ? 12 : o.rise;
        this.fall = o.fall == null ? 10 : o.fall;
        this.confirmMs = o.confirmMs == null ? 160 : o.confirmMs;
        this.refractoryMs = o.refractoryMs == null ? 1500 : o.refractoryMs;
        this.floor = o.floor == null ? -60 : o.floor;
        this._hist = [];      // [t, db] of the last ~80 ms
        this._cand = null;    // {t, db}
        this._last = -1e9;
    }
    /** Noise floor from a calibration run: the 80th percentile of levels. */
    calibrate(levels) {
        const a = (levels || []).filter(x => isFinite(x)).slice().sort((x, y) => x - y);
        if (a.length) this.floor = a[Math.min(a.length - 1, Math.floor(a.length * 0.8))];
        return this.floor;
    }
    /** Level a clap must reach: floor + 12..32 dB (sensitivity 1..0), never below −45 dBFS. */
    threshold() {
        const margin = 32 - 20 * Math.min(1, Math.max(0, this.sensitivity));
        return Math.max(this.floor + margin, -45);
    }
    push(db, t) {
        const h = this._hist;
        h.push([t, db]);
        while (h.length && t - h[0][0] > 80) h.shift();
        if (this._cand) {
            if (db > this._cand.db) { this._cand.db = db; }       // still rising — follow the peak
            if (this._cand.db - db >= this.fall) {
                const fired = t - this._last >= this.refractoryMs;
                this._cand = null;
                if (fired) { this._last = t; return true; }
                return false;
            }
            if (t - this._cand.t > this.confirmMs) this._cand = null;   // sustained — a voice, not a clap
            return false;
        }
        if (db >= this.threshold()) {
            let lo = db;
            for (let i = 0; i < h.length - 1; i++) if (h[i][1] < lo) lo = h[i][1];
            if (db - lo >= this.rise) this._cand = { t, db };
        }
        return false;
    }
}

/* ───────────────────────── photo booth sheet ───────────────────────── */

export const BOOTH_DPI = 300;
/**
 * Print geometry of a photo-booth result at 300 dpi.
 *
 *   strip   — the classic 2×6″ strip (600×1800): N photos stacked, caption at
 *             the bottom. Output 'sheet' puts TWO strips on a 4×6″ (10×15 cm)
 *             print, the way booths print and cut them.
 *   grid    — 2 columns on a 6×4″ landscape print (1800×1200).
 *   polaroid — instant-film frames (square photo, thick bottom) on a 4×6″
 *             portrait print, one frame per shot, each slightly turned.
 *
 * Returns { W, H, cells:[{x,y,w,h,rot}], captions:[{x,y,w,h}], frames?:[…] }
 * in pixels; every cell has the same size so every shot is cropped alike.
 */
export function boothLayout(layout, n, output) {
    n = Math.max(1, Math.min(8, n | 0 || 4));
    if (layout === 'grid') return gridLayout(n);
    if (layout === 'polaroid') return polaroidLayout(n);
    return stripLayout(n, output === 'sheet');
}

function stripLayout(n, sheet) {
    const SW = 600, SH = 1800, m = 36, gap = 24, capH = 190;
    const cw = SW - 2 * m;
    const ch = Math.floor((SH - m - capH - gap * (n - 1)) / n);
    const one = [];
    for (let i = 0; i < n; i++) one.push({ x: m, y: m + i * (ch + gap), w: cw, h: ch, rot: 0 });
    const cap = { x: m, y: SH - capH, w: cw, h: capH - 20 };
    if (!sheet) return { W: SW, H: SH, cells: one, captions: [cap], strips: 1 };
    const cells = [], captions = [];
    for (let s = 0; s < 2; s++) {
        const dx = s * SW;
        for (const c of one) cells.push({ x: c.x + dx, y: c.y, w: c.w, h: c.h, rot: 0 });
        captions.push({ x: cap.x + dx, y: cap.y, w: cap.w, h: cap.h });
    }
    return { W: SW * 2, H: SH, cells, captions, strips: 2, cut: [{ x: SW, y: 0, x2: SW, y2: SH }] };
}

function gridLayout(n) {
    const W = 1800, H = 1200, m = 48, gap = 28, capH = 160;
    const cols = n === 1 ? 1 : 2, rows = Math.ceil(n / cols);
    const cw = Math.floor((W - 2 * m - gap * (cols - 1)) / cols);
    const ch = Math.floor((H - m - capH - gap * (rows - 1)) / rows);
    const cells = [];
    for (let i = 0; i < n; i++) {
        const c = i % cols, r = Math.floor(i / cols);
        cells.push({ x: m + c * (cw + gap), y: m + r * (ch + gap), w: cw, h: ch, rot: 0 });
    }
    // an odd last photo sits in the middle of its row
    if (n % cols === 1 && cols === 2) cells[n - 1].x = Math.floor((W - cw) / 2);
    return { W, H, cells, captions: [{ x: m, y: H - capH + 10, w: W - 2 * m, h: capH - 30 }], strips: 1 };
}

function polaroidLayout(n) {
    const W = 1200, H = 1800;
    const cols = n <= 1 ? 1 : 2, rows = Math.ceil(n / cols);
    // captions are written on each frame's thick bottom edge, so the frames get the whole sheet
    const areaTop = 30, capH = 30, areaH = H - areaTop - capH;
    const slotW = Math.floor(W / cols), slotH = Math.floor(areaH / rows);
    // instant-film proportions: frame 88×107 mm, picture 79×79 mm
    const fw = Math.floor(Math.min(slotW * 0.86, slotH * 0.86 * 88 / 107));
    const fh = Math.round(fw * 107 / 88);
    const pw = Math.round(fw * 79 / 88), side = Math.round((fw - pw) / 2);
    const cells = [], frames = [];
    const tilt = [-3, 2.5, 2, -2.5, -1.5, 3, -2, 1.5];
    for (let i = 0; i < n; i++) {
        const c = i % cols, r = Math.floor(i / cols);
        let cx = c * slotW + Math.floor((slotW - fw) / 2);
        if (n % cols === 1 && cols === 2 && i === n - 1) cx = Math.floor((W - fw) / 2);
        const cy = areaTop + r * slotH + Math.floor((slotH - fh) / 2);
        const rot = n === 1 ? 0 : tilt[i % tilt.length];
        frames.push({ x: cx, y: cy, w: fw, h: fh, rot });
        cells.push({ x: cx + side, y: cy + side, w: pw, h: pw, rot });
    }
    return { W, H, cells, frames, captions: [], strips: 1 };
}

/* ───────────────────────── night mode stacking ───────────────────────── */

/** Add an RGBA frame into a Float32 RGB sum (length w*h*3). */
export function accumulate(sum, rgba) {
    for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
        sum[j] += rgba[i]; sum[j + 1] += rgba[i + 1]; sum[j + 2] += rgba[i + 2];
    }
    return sum;
}

/**
 * Average the stack into RGBA bytes. `gain` brightens linearly, `lift` is a
 * gentle shadow lift (a gamma below 1) — a dark average needs both to look
 * like a night photo rather than a quieter dark one.
 */
export function finalizeStack(sum, n, out, gain, lift) {
    const g = gain > 0 ? gain : 1, inv = 1 / Math.max(1, n);
    const gam = lift > 0 ? 1 / (1 + lift) : 1;
    for (let i = 0, j = 0; j < sum.length; i += 4, j += 3) {
        for (let c = 0; c < 3; c++) {
            let v = (sum[j + c] * inv * g) / 255;
            if (v > 1) v = 1;
            if (gam !== 1) v = Math.pow(v, gam);
            out[i + c] = Math.round(v * 255);
        }
        out[i + 3] = 255;
    }
    return out;
}

/* ───────────────────────── time-lapse ───────────────────────── */

/** How long the clip plays: frames / fps, and the speed-up it represents. */
export function timelapsePlan(frames, intervalS, fps) {
    const f = Math.max(0, frames | 0), p = fps > 0 ? fps : 24, iv = intervalS > 0 ? intervalS : 1;
    return { seconds: f / p, speedup: Math.round(iv * p), realSeconds: f * iv };
}

/** «0:12», «1:05», «1:02:03» — locale-free, for countdowns and durations. */
export function formatDuration(sec) {
    sec = Math.max(0, Math.round(sec));
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    return (h ? h + ':' + pad(m) : m) + ':' + pad(s);
}
